import React, { ReactNode } from "react";
import { Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { CustomDataGridTooltip } from "../components/editor/EditorItemTable";
import { isEmptyObj } from "../utilities/common";
import type { ApiEndpoint, Config, MediaItemDetailed, OptionsData, RelatedEntityItem } from "../types";
import MultiSelectDialog from "../components/common/MultiSelectDialog";

function getRelatedItem(relations: Record<string, RelatedEntityItem[]> | undefined, endpoint: ApiEndpoint) {
  // V2: data is keyed by tableName in byTableSlug; V1: keyed by relationshipKey
  return relations?.[endpoint.tableName] || relations?.[endpoint?.relationshipKey];
}

const FallbackText = React.forwardRef<HTMLSpanElement, { children?: ReactNode }>(({ children, ...props }, ref) => {
  return (
    <Typography ref={ref} {...props} sx={{ color: "#98A2B3!important", fontSize: "0.8125rem", fontStyle: "italic" }}>
      {children || "Click to add"}
    </Typography>
  );
});

export function generateRelationshipColumn({
  endpoint,
  optionsRef,
  onCreateNewRelationEntity,
  renderHeader,
  config,
  currentEndpoint,
  onSaved
}: {
  endpoint: ApiEndpoint;
  optionsRef: React.RefObject<OptionsData | null>;
  onCreateNewRelationEntity?: (name: string, onSuccess?: (newItemId: number) => void) => void;
  renderHeader?: (label: string, field: string, showFilldown?: boolean) => ReactNode;
  config: Config;
  currentEndpoint?: ApiEndpoint;
  onSaved?: (ids: number[], rowId?: string | number) => void;
}): GridColDef {
  const fieldName = endpoint.tableName;
  const isFulcrumEndpoint =
    endpoint?.dataSourceType?.toLowerCase() === "fulcrum" || currentEndpoint?.dataSourceType?.toLowerCase() === "fulcrum";

  // "Restrictions" is not a relationship — the row/media response carries it as a
  // top-level `restrictions: [{id, name}]` field, and edits are PATCHed directly
  // onto the row/media item (`restrictions: number[]`), not via anchor-relationships.
  const isRestrictionsColumn = endpoint.tableName === "restrictions";

  return {
    field: fieldName,
    headerName: endpoint.name,
    // hide filldown icon for fulcrum
    renderHeader: () => renderHeader?.(endpoint.name, fieldName, !isFulcrumEndpoint),
    flex: 1,
    maxWidth: 200,
    minWidth: 120,
    editable: !isFulcrumEndpoint,
    sortable: false,
    // MUI X v7 valueGetter signature: (value, row, column, apiRef) — `value` is the
    // raw cell value, `row` is the full row object (there is no `params.row` here).
    valueGetter: (_value: unknown, row: MediaItemDetailed) => {
      try {
        if (isRestrictionsColumn) {
          return ((row as any)?.restrictions?.map((item: any) => item.id) ?? []) as number[];
        }

        if (!row?.relationships) return [];

        // Use entityType from config to determine location in relationships.
        // V2 (post-normaliseCmTableRow): data is in byTableSlug[tableName]
        // V1 fallback: data is in geo_entities/related_entities[tableName]
        const rels = row.relationships;
        switch (endpoint.entityType) {
          case "geo":
            return (
              rels?.byTableSlug?.[endpoint.tableName]?.map((item: any) => item.id) ||
              rels?.geo_entities?.[endpoint.tableName]?.map((item: any) => item.id) ||
              []
            );
          case "media":
            return rels?.media?.map((item: any) => item.id) || [];
          default: // 'related' or undefined
            return (
              rels?.byTableSlug?.[endpoint.tableName]?.map((item: any) => item.id) ||
              rels?.related_entities?.[endpoint.tableName]?.map((item: any) => item.id) ||
              []
            );
        }
      } catch (error) {
        console.error(`Error getting value for ${endpoint.name}:`, error);
        return [];
      }
    },
    renderCell: (params: GridRenderCellParams<MediaItemDetailed>) => {
      try {
        const isFirstRow = params.api.getRowIndexRelativeToVisibleRows(params.id) === 0;

        let relationshipItems: RelatedEntityItem[] = [];

        if (isRestrictionsColumn) {
          relationshipItems = ((params.row as any)?.restrictions ?? []) as RelatedEntityItem[];
        } else {
          const rels = params.row?.relationships;
          const isEmpty =
            !rels ||
            (isEmptyObj(rels?.media || []) &&
              isEmptyObj(rels?.geo_entities || {}) &&
              isEmptyObj(rels?.related_entities || {}) &&
              isEmptyObj(rels?.byTableSlug || {}));

          if (isFirstRow && isFulcrumEndpoint && isEmpty) {
            return (
              <CustomDataGridTooltip title='Fulcrum data is not editable'>
                <FallbackText>Not editable</FallbackText>
              </CustomDataGridTooltip>
            );
          }

          if (isFirstRow && isEmpty && !isFulcrumEndpoint) {
            return <FallbackText />;
          }

          // Use entityType to get items from correct location.
          // Always check byTableSlug first (V2 normalised format), then fall back to V1 buckets.
          switch (endpoint?.entityType) {
            case "geo":
              relationshipItems =
                rels?.byTableSlug?.[endpoint.tableName] ||
                getRelatedItem(rels?.geo_entities, endpoint) ||
                getRelatedItem(rels?.related_entities, endpoint) ||
                [];
              break;
            case "media":
              relationshipItems = rels?.media || [];
              break;
            default: // 'related', cmtabledata, or undefined
              relationshipItems =
                rels?.byTableSlug?.[endpoint.tableName] ||
                getRelatedItem(rels?.related_entities, endpoint) ||
                getRelatedItem(rels?.geo_entities, endpoint) ||
                [];
          }
        }

        // If no items, just return empty string
        if (relationshipItems.length === 0) {
          if (isFirstRow) {
            if (isFulcrumEndpoint) {
              return (
                <CustomDataGridTooltip title='Fulcrum data is not editable'>
                  <FallbackText>Not editable</FallbackText>
                </CustomDataGridTooltip>
              );
            }
            return <FallbackText />;
          }
          return "";
        }

        // Find option items for display with fallback
        const itemsWithNames = relationshipItems.map((item: RelatedEntityItem) => {
          if (isRestrictionsColumn) {
            return { ...item, displayName: item.name || `ID: ${item.id}` };
          }
          const optionItem = optionsRef.current?.[endpoint.tableName]?.find((opt) => opt.id === item.id);
          const name = optionItem?.name || item.name || item.entity_name || `ID: ${item.id}`;
          return { ...item, displayName: name };
        });

        // Create tooltip content - more detailed view of the selected items
        const tooltipContent = (
          <Box
            sx={{
              width: "100%",
              maxHeight: "30rem",
              minWidth: "8rem",
              pr: "0.5rem",
              maxWidth: "24rem",
              overflowY: "auto",
              opacity: isFulcrumEndpoint ? 0.5 : 1
            }}
            className='scroller'
          >
            {isFulcrumEndpoint && (
              <Typography variant='h3' fontSize='0.875rem' color='error' fontWeight={600} py={"0.5rem"}>
                Fulcrum data is not editable.
              </Typography>
            )}

            <Typography variant='body1' fontSize='0.875rem' fontWeight={500} py={"0.5rem"}>
              {endpoint.name} ({itemsWithNames.length})
            </Typography>
            <List dense disablePadding>
              {itemsWithNames.map((item, idx) => (
                <ListItem key={`${item.id}-${idx}`} dense disablePadding>
                  <ListItemText
                    primary={<CustomDataGridTooltip title={item.displayName}>{<span>{item.displayName}</span>}</CustomDataGridTooltip>}
                    secondary={item.rel_description ? item.rel_description : null}
                    primaryTypographyProps={{
                      variant: "subtitle1",
                      fontSize: "0.875rem",
                      overflow: "hidden",
                      display: "-webkit-box",
                      className: "line-clamp-2"
                    }}
                    secondaryTypographyProps={{ variant: "caption", mt: 4 }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );

        // Return cell with tooltip
        return (
          <CustomDataGridTooltip
            title={tooltipContent}
            arrow={false}
            componentsProps={{
              tooltip: {
                sx: {
                  paddingRight: 0
                }
              }
            }}
          >
            <Typography
              variant='caption'
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                fontSize: "0.75rem",
                display: "block"
              }}
            >
              {itemsWithNames.map((item) => item.displayName).join(", ")}
            </Typography>
          </CustomDataGridTooltip>
        );
      } catch (error) {
        console.error("Error rendering cell:", error);
        return <Typography color='error'>Error</Typography>;
      }
    },
    renderEditCell: (params) => {
      if (isFulcrumEndpoint) return;
      try {
        return (
          <MultiSelectDialog
            params={params}
            options={optionsRef.current?.[endpoint.tableName] || []}
            onAddNew={(onSuccess) => onCreateNewRelationEntity?.(endpoint.name, onSuccess)}
            category={endpoint.name}
            config={config}
            onSaved={onSaved ? (ids) => onSaved(ids, params.id) : undefined}
          />
        );
      } catch (error) {
        console.error("Error rendering edit cell:", error);
        return null;
      }
    },
    cellClassName: (params) => {
      // Add error styling if this item failed to upload
      if (params.row.uploadError) {
        return "error-cell";
      }
      return "";
    }
  };
}
